import styled, { css } from 'styled-components';

const PLACEHOLDER_COLOR = '#676680';

const FONT_SANS =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export const FieldLabel = styled.div`
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  margin-bottom: 4px;
`;

export const ArtifactsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;

  & > ${FieldLabel} {
    margin-bottom: 0;
  }
`;

export const TextInput = styled.input.attrs({ className: 'nowheel' })`
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerHigh ?? '#1F2030'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  border-radius: 999px;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  font: 500 13px/1.35 ${FONT_SANS};
  padding: 7px 14px;
  outline: none;
  transition: border-color 120ms ease, background 120ms ease;

  &::placeholder {
    color: ${PLACEHOLDER_COLOR};
    opacity: 1;
  }

  &:focus {
    border-color: ${({ theme: { colors = {} } }) =>
      colors.primary ?? '#C1C1FF'};
  }

  /* Native number spinners can't be themed and clash with the dark pill. */
  &[type='number'] {
    -moz-appearance: textfield;
  }

  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const TextArea = styled.textarea.attrs({ className: 'nowheel' })`
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerHigh ?? '#1F2030'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  border-radius: 14px;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  font: 400 13px/1.45 ${FONT_SANS};
  padding: 10px 14px;
  outline: none;
  resize: vertical;
  min-height: 64px;
  max-height: 220px;
  transition: border-color 120ms ease, background 120ms ease;

  &::placeholder {
    color: ${PLACEHOLDER_COLOR};
    opacity: 1;
  }

  &:focus {
    border-color: ${({ theme: { colors = {} } }) =>
      colors.primary ?? '#C1C1FF'};
  }
`;

export const SelectWrap = styled.div`
  position: relative;
  display: block;
`;

// Opt-in class so `NodeCardShell.Body`'s input-recess rule applies. `nodrag`
// / `nopan` keep React Flow from hijacking the pointer (node drag / canvas pan)
// when the control is clicked inside a node.
export const SelectBtn = styled.button.attrs({
  className: 'ns-edit__select-btn nodrag nopan',
})`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerHigh ?? '#1F2030'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  border-radius: 999px;
  padding: 7px 10px 7px 14px;
  font: 500 13px/1.35 ${FONT_SANS};
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  outline: none;
  cursor: pointer;
  text-align: left;
  transition: border-color 120ms ease, background 120ms ease;

  &:focus-visible,
  &[aria-expanded='true'] {
    border-color: ${({ theme: { colors = {} } }) =>
      colors.primary ?? '#C1C1FF'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    border-color: ${({ theme: { colors = {} } }) =>
      colors.outlineVariant ?? '#25263A'};
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainer ?? '#181927'};
    color: ${({ theme: { colors = {} } }) =>
      colors.onSurfaceVariant ?? '#A1A3B8'};
  }

  &:disabled:focus-visible,
  &:disabled[aria-expanded='true'] {
    border-color: ${({ theme: { colors = {} } }) =>
      colors.outlineVariant ?? '#25263A'};
  }
`;

export const SelectValue = styled.span<{ $placeholder?: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ $placeholder }) =>
    $placeholder &&
    css`
      color: ${PLACEHOLDER_COLOR};
    `}
`;

export const SelectCaret = styled.span`
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  font-size: 18px;
  transition: transform 140ms ease;

  ${SelectBtn}[aria-expanded='true'] & {
    transform: rotate(180deg);
  }
`;

export const SelectMenu = styled.div.attrs({ className: 'nowheel nodrag nopan' })<{
  $portal?: boolean;
}>`
  position: ${({ $portal }) => ($portal ? 'fixed' : 'absolute')};
  ${({ $portal }) =>
    !$portal &&
    css`
      top: calc(100% + 4px);
      left: 0;
      right: 0;
    `}
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerHigh ?? '#1F2030'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  border-radius: 24px;
  padding: 6px;
  max-height: 280px;
  overflow-y: auto;
  z-index: ${({ $portal }) => ($portal ? 60 : 30)};
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
`;

// Combobox trigger for a searchable SelectField: the pill IS the text input
// (closed shows the current label, open clears so the user types in place).
// Matches SelectBtn's look with extra right padding to clear the caret.
export const SelectInput = styled.input.attrs({
  type: 'text',
  className: 'ns-edit__select-btn nodrag nopan',
})`
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerHigh ?? '#1F2030'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  border-radius: 999px;
  padding: 7px 34px 7px 14px;
  font: 500 13px/1.35 ${FONT_SANS};
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  outline: none;
  cursor: text;
  text-overflow: ellipsis;
  transition: border-color 120ms ease, background 120ms ease;

  &::placeholder {
    color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
    opacity: 0.75;
  }

  &:focus {
    border-color: ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainer ?? '#181927'};
  }
`;

// Caret overlaid on the right of SelectInput (the input can't hold children).
export const SelectInputCaret = styled.span<{ $open?: boolean }>`
  position: absolute;
  right: 12px;
  top: 50%;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  font-size: 18px;
  pointer-events: none;
  transition: transform 140ms ease;
  transform: translateY(-50%)
    ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

export const SelectEmpty = styled.div`
  padding: 8px 12px;
  font: 500 13px/1.35 ${FONT_SANS};
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#A1A3B8'};
`;

export const SelectOption = styled.button<{
  $active?: boolean;
  $highlighted?: boolean;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  border: 0;
  border-radius: 18px;
  padding: 8px 12px;
  font: 500 13px/1.35 ${FONT_SANS};
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  background: ${({ $active, theme: { colors = {} } }) =>
    $active
      ? `color-mix(in oklch, ${colors.primary ?? '#C1C1FF'} 14%, transparent)`
      : 'transparent'};
  cursor: pointer;
  text-align: left;

  /* The keyboard cursor has to read differently from a selected row, which
     already carries the $active tint. */
  outline: ${({ $highlighted, theme: { colors = {} } }) =>
    $highlighted ? `2px solid ${colors.primary ?? '#C1C1FF'}` : 'none'};
  outline-offset: -2px;

  &:hover {
    background: ${({ theme: { colors = {} } }) =>
      `color-mix(in oklch, ${colors.primary ?? '#C1C1FF'} 10%, transparent)`};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
    color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#A1A3B8'};
  }

  &:disabled:hover {
    background: transparent;
  }
`;

/**
 * @deprecated — kept only for CHECKBOX fields that still use a native
 * control; all SELECT / TEXT / TEXTAREA cases now use the custom styled
 * counterparts above.
 */
export const Select = styled.select`
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerHigh ?? '#1F2030'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  border-radius: 999px;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  font: 500 13px/1.35 ${FONT_SANS};
  padding: 7px 14px;
  outline: none;
  appearance: none;
`;

export const RequiredCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};

  & > input {
    margin: 0;
  }
`;

export const ArtifactsStrip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 4px;
`;

/**
 * Positioning context for {@link GeneratingOverlay} over a node's preview
 * (image slot / artifact strip). Only needed where the preview itself
 * isn't already a relative box. {@code $reserve} keeps a minimum height
 * so the spinner is visible on the very first generation, before any
 * artifact exists to hold.
 */
export const PreviewOverlayHost = styled.div<{ $reserve?: boolean }>`
  position: relative;
  ${({ $reserve }) => ($reserve ? 'min-height: 88px;' : '')}
`;

/**
 * Translucent scrim + spinner shown over a generation node's preview while
 * a run that will (re)compute it is in flight. Sits over the previously
 * generated output (held forward) so the user sees "generating" instead of
 * a flash to an older artifact (AIB-7181). Non-interactive so hover actions
 * underneath keep working once it clears.
 */
export const GeneratingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
  z-index: 2;
  border-radius: inherit;
  background: ${({ theme: { colors = {} } }) =>
    `color-mix(in oklch, ${colors.surface ?? '#0E0F18'} 42%, transparent)`};

  .material-symbols-rounded {
    font-size: 30px;
    line-height: 30px;
    color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
    animation: node-preview-spin 1s linear infinite;
  }

  @keyframes node-preview-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
